import { CdkVirtualForOf, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  HolidayRegionCode,
  TimelineTimeframe,
  UserTimelinesModal,
} from '@wilson/interfaces';
import {
  GanttChartDayDemarcatorComponent,
  GanttDimensionDataSourceService,
  OvertimeDataSourceService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  ModalInteractionService,
  ShiftTimelineModule,
  TimelineFilterUserSkippedActivitiesPipe,
} from '@wilson/shift-timeline/core';
import { map, Observable, Subscription } from 'rxjs';
import { FindStaffGanttChartService } from '../services/find-staff-gantt-chart.service';
import { FindStaffOvertimeDataSourceServiceService } from '../services/find-staff-overtime-data-source-service.service';
import { UserTimelineInfoRadioGroupComponent } from '../user-timeline-info-radio-group/user-timeline-info-radio-group.component';
import { TimelineModalUserViewComponent } from '../timeline-modal-user-view/timeline-modal-user-view.component';
import { TimelineModalOverlapLevelsPipe } from '../../pipes/timeline-modal-overlap-levels.pipe';
import { ShiftAssignmentModalState } from '@wilson/shift-timeline/state';
import { Store } from '@ngxs/store';
import { eachDayOfInterval } from 'date-fns';

@Component({
  selector: 'wilson-timeline-modal-row',
  templateUrl: './timeline-modal-row.component.html',
  styleUrls: ['./timeline-modal-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: OvertimeDataSourceService,
      useClass: FindStaffOvertimeDataSourceServiceService,
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    TimelineFilterUserSkippedActivitiesPipe,
    TimelineModalOverlapLevelsPipe,
    GanttChartDayDemarcatorComponent,
    ScrollingModule,
    UserTimelineInfoRadioGroupComponent,
    ShiftTimelineModule,
    TimelineModalUserViewComponent,
  ],
})
export class TimelineModalRowComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly cdr = inject(ChangeDetectorRef);
  readonly store = inject(Store);
  readonly modalInteractionService = inject(ModalInteractionService);
  private readonly findStaffGanttChartService = inject(
    FindStaffGanttChartService,
  );
  private readonly ganttDimensionDataSourceService = inject(
    GanttDimensionDataSourceService,
  );

  public mouseOverRowIndex$!: Observable<null | number>;
  public frameLength$!: Observable<number>;
  @Input()
  userTimelinesInfo: UserTimelinesModal[] | null = [];
  @Input()
  stickyHasShadow?: boolean;

  @ViewChild(CdkVirtualForOf)
  cdkVirtualForOf!: CdkVirtualForOf<UserTimelinesModal[]>;

  protected daysRange$!: Observable<Date[]>;
  protected visibleDaysRange$!: Observable<TimelineTimeframe>;
  protected publicHolidaysRegion$!: Observable<HolidayRegionCode>;
  protected resourcesColumnWidthPx$!: Observable<number>;
  protected defaultRowHeight!: number;
  protected renderRadioInput = true;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.visibleDaysRange$ =
      this.ganttDimensionDataSourceService.visibleDaysRangeStream();
    this.daysRange$ = this.ganttDimensionDataSourceService
      .visibleDaysRangeStream()
      .pipe(map((timeframe) => eachDayOfInterval(timeframe)));
    this.frameLength$ =
      this.ganttDimensionDataSourceService.frameLengthStream();
    this.publicHolidaysRegion$ =
      this.findStaffGanttChartService.publicHolidaysRegion$;
    this.resourcesColumnWidthPx$ = this.store.select(
      ShiftAssignmentModalState.resourcesColumnWidthPx,
    );
    this.defaultRowHeight =
      this.findStaffGanttChartService.ganttChartDefaultRowHeight;
  }

  ngAfterViewInit(): void {
    const forceRerenderSub = this.cdkVirtualForOf.viewChange.subscribe(() => {
      setTimeout(() => {
        this.cdr.detectChanges();
      });
    });
    this.subscriptions.add(forceRerenderSub);
  }

  trackById(_: number, user: UserTimelinesModal): string | undefined {
    return user.id;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
